import type { ReactElement } from 'react'
import type { DisruptionPresentation } from './store'
import { defaultStrings } from './strings'
import type { SuggesterStrings } from './strings'
import styles from './DisruptionDetails.module.css'

/**
 * Minimal line descriptor the affected-lines chips need (spec §5 / R4).
 * The host resolves each `lineId` from its schema-line map; chips whose
 * id does NOT resolve are dropped silently — better fewer correct chips
 * than a raw `line-cen` string.
 */
export interface DisruptionLine {
  /** Display name (e.g. "Northern"). */
  readonly localName: string
  /** Hex colour of the line dot. */
  readonly hexColor: string
}

/**
 * Props for {@link DisruptionDetails}.
 */
export interface DisruptionDetailsProps {
  /** Presentation state (station + severity + sub-phase + payload). */
  readonly presentation: DisruptionPresentation
  /**
   * Schema line map (`lineId` → {@link DisruptionLine}) — resolves the
   * affected-lines chips (spec §5, R4). Unresolved ids are dropped.
   */
  readonly lines: Readonly<Record<string, DisruptionLine>>
  /** Back control / Escape — pops Details, returns to the picker. */
  readonly onBack: () => void
  /** Retry control in the Error sub-state — re-enters Loading. */
  readonly onRetry: () => void
  /** Localised string set. @defaultValue {@link defaultStrings} */
  readonly strings?: SuggesterStrings
  /**
   * TASK-412 O3: in the tablet/desktop side panel the host supplies a
   * translucent liquid-glass surface, so Disruption Details must render
   * its content WITHOUT its own opaque `background-primary` plate (else a
   * solid plate covers the glass — the web equivalent of the iOS
   * NavigationStack-bg bug). `true` drops the screen background so the
   * panel glass shows through; the mobile modal (`false`, default) keeps
   * its opaque surface. Passed explicitly by the host (not context), so a
   * portal boundary can't drop it.
   */
  readonly transparent?: boolean
}

/**
 * Disruption Details screen (spec §5) — pushed over the picker when the
 * user taps a Closed / Advisory row's status composite. Pixel-parity
 * with the Figma frames `6479:22428` (dark) / `6480:23436` (light).
 *
 * Three sub-phases (driven by `presentation.phase`):
 * - **rich** — station identity + severity chip + Type / Active /
 *   Affected-lines meta + "What's happening" body + TfL source line.
 * - **loading** — shimmer skeleton (severity known, payload pending).
 * - **error** — soft "We couldn't load the latest info" + Retry.
 *
 * The header (back chevron + "Station details") is shown on every phase.
 * Mirrors the iOS `DisruptionDetailsView`.
 */
export function DisruptionDetails({
  presentation,
  lines,
  onBack,
  onRetry,
  strings = defaultStrings,
  transparent = false,
}: DisruptionDetailsProps): ReactElement {
  const { station, severity, phase, status } = presentation

  return (
    <div
      className={transparent ? `${styles.screen} ${styles.screenTransparent}` : styles.screen}
      data-testid="suggester-disruption-details"
    >
      <header className={styles.header}>
        <button
          type="button"
          className={styles.back}
          data-testid="suggester-disruption-back"
          aria-label={strings.disruptionBack}
          onClick={onBack}
        >
          <svg width="11" height="18" viewBox="0 0 11 18" aria-hidden focusable="false" fill="none">
            <path
              d="M9 1 L2 9 L9 17"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className={styles.title}>{strings.disruptionTitle}</h1>
        <span className={styles.headerSpacer} aria-hidden />
      </header>

      {phase === 'loading' && <LoadingBody />}
      {phase === 'error' && <ErrorBody strings={strings} onRetry={onRetry} />}
      {phase === 'rich' && (
        <RichBody
          name={station.name}
          lineName={station.lineName}
          ringColor={station.lineHexColor}
          severity={severity}
          status={status}
          lines={lines}
          strings={strings}
        />
      )}
    </div>
  )
}

/** Rich Details body — the full disruption breakdown (Figma 6479:22428). */
function RichBody({
  name,
  lineName,
  ringColor,
  severity,
  status,
  lines,
  strings,
}: {
  name: string
  lineName?: string
  ringColor: string
  severity: DisruptionPresentation['severity']
  status: DisruptionPresentation['status']
  lines: Readonly<Record<string, DisruptionLine>>
  strings: SuggesterStrings
}): ReactElement {
  const chips = (status?.lineIds ?? [])
    .map((id) => ({ id, line: lines[id] }))
    .filter((entry): entry is { id: string; line: DisruptionLine } => entry.line !== undefined)
  const active = formatActiveWindow(status?.fromDate, status?.toDate)

  return (
    <div className={styles.body}>
      <div className={styles.identity}>
        <span className={styles.ringBox} aria-hidden>
          <span className={styles.ring} style={{ borderColor: ringColor }} />
        </span>
        <div className={styles.identityLabels}>
          <span className={styles.identityName}>{name}</span>
          {lineName !== undefined && <span className={styles.identityLine}>{lineName}</span>}
        </div>
      </div>

      <span
        className={`${styles.severityChip} ${
          severity === 'closure' ? styles.severityClosure : styles.severityAdvisory
        }`}
      >
        <span className={styles.severityChipLabel}>
          {severity === 'closure' ? strings.stationClosed : strings.serviceAlert}
        </span>
        {/* Severity glyph — matches the Figma chip (closure ⊘ / advisory ⚠). */}
        {severity === 'closure' ? <ChipNoSignIcon /> : <ChipWarningIcon />}
      </span>

      {status?.title !== undefined && (
        <div className={styles.meta}>
          <span className={styles.metaLabel}>{strings.disruptionType}</span>
          {/* The "Type" row shows the human-readable headline (`title`,
              e.g. "Planned work") — matching Figma 6531:23437 and iOS
              (DisruptionDetailsView.swift:101, `payload.title`). NOT
              `appearance`, which is the opaque TFL tag (`PlannedClosure`
              / `RealTime`). Cross-platform contract (QA D1). */}
          <span className={styles.metaValue}>{status.title}</span>
        </div>
      )}

      {active !== undefined && (
        <div className={styles.meta}>
          <span className={styles.metaLabel}>{strings.disruptionActive}</span>
          <span className={styles.metaValue}>{active}</span>
        </div>
      )}

      {chips.length > 0 && (
        <div className={styles.meta}>
          <span className={styles.metaLabel}>{strings.disruptionAffectedLines}</span>
          <div className={styles.chips}>
            {chips.map(({ id, line }) => (
              <span
                key={id}
                className={styles.lineChip}
                data-testid={`suggester-disruption-line-${id}`}
              >
                <span
                  className={styles.lineDot}
                  style={{ background: line.hexColor }}
                  aria-hidden
                />
                <span className={styles.lineChipLabel}>{line.localName}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {status?.message !== undefined && (
        <>
          <h2 className={styles.bodyHeading}>{strings.disruptionWhatsHappening}</h2>
          <p className={styles.bodyText}>{status.message}</p>
        </>
      )}

      <p className={styles.source}>{strings.disruptionSource}</p>
    </div>
  )
}

/** Loading shimmer skeleton (Figma 6532:22548). */
function LoadingBody(): ReactElement {
  return (
    <div className={styles.body} data-testid="suggester-disruption-loading" aria-hidden>
      <div className={styles.skelIdentity}>
        <span className={styles.skelRing} />
        <div className={styles.skelLabels}>
          <span className={styles.skelLine} style={{ width: '60%' }} />
          <span className={styles.skelLine} style={{ width: '35%' }} />
        </div>
      </div>
      <span className={styles.skelChip} />
      {[70, 80, 75, 90].map((w, i) => (
        <span key={i} className={styles.skelBlock} style={{ width: `${w}%` }} />
      ))}
    </div>
  )
}

/** Error sub-state (Figma 6534:22788) — soft tone + Retry. */
function ErrorBody({
  strings,
  onRetry,
}: {
  strings: SuggesterStrings
  onRetry: () => void
}): ReactElement {
  return (
    <div className={styles.errorBody} data-testid="suggester-disruption-error">
      <div className={styles.errorCenter}>
        {/* Magnifier centred in the 44-box (QA D2): the glyph bounding box
            (circle r=11.5 around 18.5,18.5 → 7..30, handle 26.5..37) spans
            ~7..37 on both axes → optical centre 22,22, not top-left biased. */}
        <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden focusable="false" fill="none">
          <circle cx="18.5" cy="18.5" r="11.5" stroke="currentColor" strokeWidth="2" />
          <path
            d="m26.5 26.5 10.5 10.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <p className={styles.errorTitle}>{strings.disruptionErrorTitle}</p>
        <p className={styles.errorSubtitle}>{strings.disruptionErrorSubtitle}</p>
      </div>
      <button
        type="button"
        className={styles.retry}
        data-testid="suggester-disruption-retry"
        onClick={onRetry}
      >
        {strings.disruptionRetry}
      </button>
    </div>
  )
}

/** Severity-chip warning triangle (advisory) — SF Symbol exclamationmark.triangle. */
function ChipWarningIcon(): ReactElement {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden focusable="false" fill="none">
      <path
        d="M6.5 1.5 L12 11.5 H1 Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      <path d="M6.5 5 V7.8" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <circle cx="6.5" cy="9.6" r="0.65" fill="currentColor" />
    </svg>
  )
}

/** Severity-chip circle-slash (closure) — SF Symbol nosign. */
function ChipNoSignIcon(): ReactElement {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden focusable="false" fill="none">
      <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.1" />
      <path d="M3 3 L10 10" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  )
}

/**
 * Compose the "Active" window string from the optional from/to dates.
 * Returns `undefined` when neither is present (the meta row is omitted).
 */
function formatActiveWindow(fromDate?: string, toDate?: string): string | undefined {
  if (fromDate !== undefined && toDate !== undefined) return `${fromDate} – ${toDate}`
  return fromDate ?? toDate
}
